import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrosoft, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { KeyRound, Lock, Mail } from 'lucide-react';

import '../../assets/styles/Login.css';
import '../../assets/styles/FormCard.css';
import { useLanguage } from '../../assets/js/LanguageContext';
import { FormField, PasswordField } from '../common/FormField';
import { GOOGLE_SIGNUP_KEY } from './GoogleSignup';
import { afterAuthPath } from '../../utils/afterAuth';
import { authErrorKey } from '../../utils/authErrors';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const LoginForm = () => {
  const [step, setStep] = useState(1);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const validatePassword = (value) => value.length >= 6;

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    setEmailError('');
    setLoginError('');

    if (value && !validateEmail(value)) {
      setEmailError(t('auth_error_email_invalid'));
    }
  };

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    setPasswordError('');
    setLoginError('');

    if (value && !validatePassword(value)) {
      setPasswordError(t('auth_error_password_short'));
    }
  };

  const handleSocialLogin = (provider) => {
    window.location.href = `${API_BASE_URL}/auth/${provider}`;
  };

  // Mis diagnósticos recoge las preguntas generadas en la landing antes de entrar
  const completeLoginRedirect = () => navigate('/dashboard');

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoginError('');
      try {
        const res = await fetch(`${API_BASE_URL}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken: tokenResponse.access_token })
        });
        const data = await res.json();
        if (!res.ok) {
          setLoginError(t('login_google_error'));
          return;
        }
        if (data.needsSignup) {
          // Cuenta nueva: falta aceptar la privacidad antes de crearla
          sessionStorage.setItem(GOOGLE_SIGNUP_KEY, JSON.stringify({ signupToken: data.signupToken, email: data.email, name: data.name }));
          navigate('/google-signup');
          return;
        }
        localStorage.setItem('token', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        window.dispatchEvent(new Event('authChange'));
        completeLoginRedirect();
      } catch (error) {
        console.error('Error en el acceso con Google:', error);
        setLoginError(t('login_google_error'));
      }
    },
    onError: () => setLoginError(t('login_google_error'))
  });

  const handleResetSubmit = async (event) => {
    event.preventDefault();

    const res = await fetch(`${API_BASE_URL}/ok-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(t(authErrorKey(res.status === 429 ? 'too_many_attempts' : data.code, 'auth_error_invalid_code')));
      return;
    }

    try {
      const loginRes = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const loginData = await loginRes.json();
      if (!loginRes.ok) {
        setMessage(t('auth_error_login_after_code'));
        return;
      }

      localStorage.setItem('token', loginData.token);
      if (loginData.refreshToken) {
        localStorage.setItem('refreshToken', loginData.refreshToken);
      }
      window.dispatchEvent(new Event('authChange'));
      navigate(afterAuthPath('/choose'));
    } catch (error) {
      console.error('Error al intentar iniciar sesión:', error);
      setMessage(t('auth_error_connection'));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setEmailError('');
    setPasswordError('');
    setLoginError('');

    let hasErrors = false;

    if (!email.trim()) {
      setEmailError(t('auth_error_email_required'));
      hasErrors = true;
    } else if (!validateEmail(email)) {
      setEmailError(t('auth_error_email_invalid'));
      hasErrors = true;
    }

    if (!password.trim()) {
      setPasswordError(t('auth_error_password_required'));
      hasErrors = true;
    } else if (!validatePassword(password)) {
      setPasswordError(t('auth_error_password_short'));
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, language })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setLoginError(t(authErrorKey(response.status === 429 ? 'too_many_attempts' : data.code, 'auth_error_generic')));
        return;
      }

      if (data.needsEmailVerification) {
        setStep(2);
        return;
      }

      localStorage.setItem('token', data.token);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      window.dispatchEvent(new Event('authChange'));
      completeLoginRedirect();
    } catch (error) {
      console.error('Error de conexión:', error);
      setLoginError(t('auth_error_connection'));
    }
  };

  if (step === 2) {
    return (
      <form className="tp-form tp-form--auth" onSubmit={handleResetSubmit}>
        <h2 className="tp-auth__title">{t('login_code_title')}</h2>
        <p className="tp-form__copy tp-auth__copy">{t('login_code_copy')}</p>
        <FormField
          id="login-code"
          label={t('login_code_placeholder')}
          hideLabel
          icon={<KeyRound />}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('login_code_placeholder')}
          inputMode="numeric"
          autoComplete="one-time-code"
          required
        />
        {message && <p className="tp-form__alert" role="alert">{message}</p>}
        <button type="submit" className="btn btn--primary tp-form__submit">
          {t('login_code_btn')}
        </button>
      </form>
    );
  }

  return (
    <form className="tp-form tp-form--auth" onSubmit={handleSubmit} noValidate>
      <h2 className="tp-auth__title">{t('login_title')}</h2>
      <FormField
        id="login-email"
        type="email"
        label={t('login_placeholder_email')}
        hideLabel
        icon={<Mail />}
        value={email}
        onChange={handleEmailChange}
        placeholder={t('login_placeholder_email')}
        autoComplete="email"
        error={emailError}
        required
      />
      <PasswordField
        id="login-password"
        label={t('login_placeholder_password')}
        hideLabel
        icon={<Lock />}
        value={password}
        onChange={handlePasswordChange}
        placeholder={t('login_placeholder_password')}
        autoComplete="current-password"
        error={passwordError}
        showLabel={t('form_show_password')}
        hideLabelText={t('form_hide_password')}
        required
      />
      <div className="tp-form__row">
        <Link to="/reset-password" className="tp-form__link">{t('login_forgot_password')}</Link>
      </div>

      {loginError && <p className="tp-form__alert" role="alert">{loginError}</p>}

      <button type="submit" className="btn btn--primary tp-form__submit">
        {t('login_title')}
      </button>

      <p className="tp-auth__social-label">{t('auth_social_login')}</p>
      <div className="tp-auth__social">
        <button
          type="button"
          className="tp-auth__social-btn"
          onClick={() => googleLogin()}
          aria-label={t('login_btn_google')}
          title={t('login_btn_google')}
        >
          <FontAwesomeIcon icon={faGoogle} />
        </button>
        <button
          type="button"
          className="tp-auth__social-btn"
          onClick={() => handleSocialLogin('microsoft')}
          aria-label={t('login_btn_microsoft')}
          title={t('login_btn_microsoft')}
        >
          <FontAwesomeIcon icon={faMicrosoft} />
        </button>
      </div>
    </form>
  );
};

export default LoginForm;
